import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import * as fileSaver from 'file-saver';

import { CollaborateurDialogComponent } from './collaborateur-dialog/collaborateur-dialog.component';
import { MatConfirmDialogComponent } from 'src/app/shared/mat-confirm-dialog/mat-confirm-dialog.component';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { ICollaborateur, CollaborateurStatutFiltre } from 'src/app/shared/models/collaborateur';
import { CollaborateurService } from './collaborateur.service';

@Component({
  selector: 'app-collaborateurs-list',
  templateUrl: './collaborateurs-list.component.html',
  styleUrls: ['./collaborateurs-list.component.less']
})
export class CollaborateursListComponent implements OnInit {

  displayedColumns: string[] = ['nom', 'prenom', 'postes', 'statut', 'dateCreation', 'cv', 'actions'];
  collaborateurs: ICollaborateur[] = [];
  loading = false;

  // ── Filtres ──────────────────────────────────────────────────────────────
  searchTerm = '';
  private searchSubject = new Subject<string>();
  statutFiltre: CollaborateurStatutFiltre = 'ACTIFS';

  // ── Pagination / tri (par défaut : les plus récents en premier) ─────────────
  pageIndex = 0;
  pageSize = 20;
  totalElements = 0;
  sortField = 'createdAt';
  sortDirection: 'asc' | 'desc' = 'desc';

  // ── CV ───────────────────────────────────────────────────────────────────
  openingCvId: string | null = null;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private collaborateurService: CollaborateurService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(350),
      distinctUntilChanged()
    ).subscribe(() => {
      this.pageIndex = 0;
      this.load();
    });

    this.load();
  }

  // ── Chargement ────────────────────────────────────────────────────────────

  load(): void {
    this.loading = true;

    const active = this.statutFiltre === 'TOUS' ? undefined : this.statutFiltre === 'ACTIFS';

    this.collaborateurService.search({
      q: this.searchTerm || undefined,
      active,
      page: this.pageIndex,
      size: this.pageSize,
      sort: `${this.sortField},${this.sortDirection}`
    }).subscribe({
      next: (page) => {
        this.collaborateurs = page.content;
        this.totalElements = page.totalElements;
        this.pageIndex = page.pageNumber;
        this.pageSize = page.pageSize;
        this.loading = false;
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors du chargement des collaborateurs'),
          'error-snack-bar'
        );
        this.loading = false;
      }
    });
  }

  // ── Filtres / tri / pagination ───────────────────────────────────────────

  onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchSubject.next(value);
  }

  onStatutChange(statut: CollaborateurStatutFiltre): void {
    this.statutFiltre = statut;
    this.pageIndex = 0;
    this.load();
  }

  onSortChange(sort: Sort): void {
    if (!sort.direction) {
      this.sortField = 'createdAt';
      this.sortDirection = 'desc';
    } else {
      this.sortField = sort.active;
      this.sortDirection = sort.direction as 'asc' | 'desc';
    }
    this.pageIndex = 0;
    this.load();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.load();
  }

  // ── Dialog création / édition ────────────────────────────────────────────

  openDialog(collaborateur: ICollaborateur | null = null): void {
    const dialogRef = this.dialog.open(CollaborateurDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      data: collaborateur
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.load();
    });
  }

  // ── Activation / désactivation (CDC règle métier §4.5-4.6) ──────────────────

  toggleActivation(collaborateur: ICollaborateur): void {
    const nextActive = !collaborateur.active;
    const action = nextActive ? 'réactiver' : 'désactiver';

    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: { message: `Voulez-vous ${action} ${collaborateur.prenom} ${collaborateur.nom} ?` }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.collaborateurService.changeActivation(collaborateur.collabId, nextActive).subscribe({
        next: () => {
          this.snackbarService.openSnackBar(
            nextActive ? 'Collaborateur réactivé avec succès' : 'Collaborateur désactivé avec succès'
          );
          this.load();
        },
        error: (err) => {
          this.snackbarService.openSnackBar(
            this.extractErrorMessage(err, `Erreur lors de la tentative de ${action}`),
            'error-snack-bar'
          );
        }
      });
    });
  }

  // ── CV ───────────────────────────────────────────────────────────────────

  postesLabel(collaborateur: ICollaborateur): string {
    if (!collaborateur.postes || collaborateur.postes.length === 0) return '';
    return collaborateur.postes.map(p => p.libelle).join(', ');
  }

  /** Ouvre le CV dans un nouvel onglet du navigateur (rendu PDF natif) */
  openCv(collaborateur: ICollaborateur): void {
    this.openingCvId = collaborateur.collabId;

    this.collaborateurService.getCvBlob(collaborateur.collabId).subscribe({
      next: (blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank');
        this.openingCvId = null;
      },
      error: (err) => {
        this.openingCvId = null;
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors de l\'ouverture du CV'),
          'error-snack-bar'
        );
      }
    });
  }

  downloadCv(collaborateur: ICollaborateur): void {
    this.collaborateurService.getCvBlob(collaborateur.collabId).subscribe({
      next: (blob) => {
        fileSaver.saveAs(blob, `CV_${collaborateur.nom}_${collaborateur.prenom}.pdf`);
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors du téléchargement du CV'),
          'error-snack-bar'
        );
      }
    });
  }

  // ── Helper erreurs ────────────────────────────────────────────────────────

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }
}